import { ECardField, ICardField, TCardField, TCardFields } from './store/listViewBoard/types';
import { getMessages } from '../../../store/constants';
import { sortCardsByStringValue } from './helpers/sortCardsByStringValue';
import { sortCardsByNumericValue } from './helpers/sortCardsByNumericValue';
import { IApplicationState, TBoardId } from '../../../types/types';
import { ECustomPropertyType } from '../../../store/customProperties/types';
import { getBoardCardCustomProperties } from '../../../store/model/board/selectors/getBoardCardCustomProperties';
import { GOOGLE_SPACING } from '../../../const';

export const CLASS_LIST_VIEW = 'list-view';
export const CLASS_LIST_VIEW_DRAGGABLE = CLASS_LIST_VIEW + '__draggable';
export const CLASS_LIST_VIEW_DRAGGABLE_DRAGGING = CLASS_LIST_VIEW_DRAGGABLE + '--dragging';

export const LIST_VIEW_ASC_DEFAULT = true;
export const LIST_VIEW_CARD_HEIGHT = 44; // px
export const LIST_VIEW_MOBILE_WIDTH = 1024; // px

export const LIST_VIEW_LOCALSTORAGE_ITEM_NAME = 'listViewSettings';

const CARD_FIELDS: TCardFields = {
    [ECardField.TITLE]: {
        isHideable: false,
        width: GOOGLE_SPACING * 43,
        isShownByDefault: true,
        title: getMessages().getText('list_view.card_field.title'),
        sortFunction: sortCardsByStringValue,
        sortField: 'name',
        value: ECardField.TITLE,
    },
    [ECardField.PRIORITY]: {
        isHideable: true,
        width: GOOGLE_SPACING * 8,
        isShownByDefault: true,
        title: getMessages().getText('list_view.card_field.priority'),
        sortFunction: sortCardsByNumericValue,
        sortField: 'priority',
        value: ECardField.PRIORITY,
    },
    [ECardField.PROGRESS]: {
        isHideable: true,
        width: GOOGLE_SPACING * 9,
        isShownByDefault: true,
        title: getMessages().getText('list_view.card_field.progress'),
        sortFunction: sortCardsByNumericValue,
        sortField: 'processingPercent',
        value: ECardField.PROGRESS,
    },
    [ECardField.CREATED]: {
        isHideable: true,
        width: GOOGLE_SPACING * 8,
        isShownByDefault: true,
        title: getMessages().getText('list_view.card_field.created'),
        sortFunction: sortCardsByNumericValue,
        sortField: 'created',
        value: ECardField.CREATED,
    },
    [ECardField.LIST]: {
        isHideable: true,
        width: GOOGLE_SPACING * 17,
        isShownByDefault: true,
        title: getMessages().getText('list_view.card_field.list'),
        sortFunction: sortCardsByStringValue,
        sortField: 'listName',
        value: ECardField.LIST,
    },
    [ECardField.TAGS]: {
        isHideable: true,
        width: GOOGLE_SPACING * 14,
        isShownByDefault: true,
        title: getMessages().getText('list_view.card_field.tags'),
        sortFunction: sortCardsByStringValue,
        sortField: 'tags',
        value: ECardField.TAGS,
    },
    [ECardField.START_DATE]: {
        isHideable: true,
        width: GOOGLE_SPACING * 10,
        isShownByDefault: true,
        title: getMessages().getText('list_view.card_field.start_date'),
        sortFunction: sortCardsByNumericValue,
        sortField: 'startDate',
        value: ECardField.START_DATE,
    },
    [ECardField.DUE_DATE]: {
        isHideable: true,
        width: GOOGLE_SPACING * 10,
        isShownByDefault: true,
        title: getMessages().getText('list_view.card_field.due_date'),
        sortFunction: sortCardsByNumericValue,
        sortField: 'dueDate',
        value: ECardField.DUE_DATE,
    },
    [ECardField.ESTIMATE]: {
        isHideable: true,
        width: GOOGLE_SPACING * 9,
        isShownByDefault: true,
        title: getMessages().getText('list_view.card_field.estimate'),
        sortFunction: sortCardsByNumericValue,
        sortField: 'estimate',
        value: ECardField.ESTIMATE,
    },
    [ECardField.SPENT_TIME]: {
        isHideable: true,
        width: GOOGLE_SPACING * 10,
        isShownByDefault: true,
        title: getMessages().getText('list_view.card_field.spent_time'),
        sortFunction: sortCardsByNumericValue,
        sortField: 'totalSpentTime',
        value: ECardField.SPENT_TIME,
    },
    [ECardField.RELATED_CARDS]: {
        isHideable: true,
        width: GOOGLE_SPACING * 9,
        isShownByDefault: true,
        title: getMessages().getText('list_view.card_field.related_cards'),
        sortFunction: sortCardsByNumericValue,
        sortField: 'relatedCardCount',
        value: ECardField.RELATED_CARDS,
    },
    [ECardField.CHECKLIST]: {
        isHideable: true,
        width: GOOGLE_SPACING * 10,
        isShownByDefault: true,
        title: getMessages().getText('list_view.card_field.checklist'),
        sortFunction: sortCardsByNumericValue,
        sortField: 'checklistStats',
        value: ECardField.CHECKLIST,
    },
    [ECardField.ATTACHMENTS]: {
        isHideable: true,
        width: GOOGLE_SPACING * 6,
        isShownByDefault: true,
        title: getMessages().getText('list_view.card_field.attachments'),
        sortFunction: sortCardsByNumericValue,
        sortField: 'driveDocCount',
        value: ECardField.ATTACHMENTS,
    },
    [ECardField.COMMENTS]: {
        isHideable: true,
        width: GOOGLE_SPACING * 10,
        isShownByDefault: true,
        title: getMessages().getText('list_view.card_field.comments'),
        sortFunction: sortCardsByNumericValue,
        sortField: 'commentCount',
        value: ECardField.COMMENTS,
    },
    [ECardField.ASSIGNEES]: {
        isHideable: true,
        width: GOOGLE_SPACING * 10,
        isShownByDefault: true,
        title: getMessages().getText('list_view.card_field.assignees'),
        sortFunction: sortCardsByStringValue,
        sortField: 'assignees',
        value: ECardField.ASSIGNEES,
    },
};

const SHOWN_FIELDS_INITIAL: ECardField[] = [
    ECardField.PRIORITY,
    ECardField.PROGRESS,
    ECardField.LIST,
    ECardField.TAGS,
    ECardField.START_DATE,
    ECardField.DUE_DATE,
    ECardField.ESTIMATE,
    ECardField.SPENT_TIME,
    ECardField.RELATED_CARDS,
    ECardField.CHECKLIST,
    ECardField.ATTACHMENTS,
    ECardField.COMMENTS,
    ECardField.ASSIGNEES,
    ECardField.CREATED,
];

export const getCardFieldsConf = (
    state: IApplicationState,
    boardId: TBoardId
): ICardField[] => {
    const cardFields: ICardField[] = [];
    for (let cardField in CARD_FIELDS) {
        cardFields.push(getCardField(cardField, state, boardId));
    }
    const customProperties = getBoardCardCustomProperties(state, boardId);
    for (let customPropertyId in customProperties) {
        cardFields.push(getCardField(customPropertyId, state, boardId));
    }
    return cardFields;
};

export const getCardField = (
    cardField: TCardField | string,
    state?: IApplicationState,
    boardId?: TBoardId,
): ICardField => {
    let cardFieldItem: ICardField;
    if (CARD_FIELDS[cardField]) {
        cardFieldItem = {...CARD_FIELDS[cardField]};
    } else if (state && boardId) {
        const customProperties = getBoardCardCustomProperties(state, boardId);
        const customProperty = customProperties[cardField];
        if (customProperty) {
            const { id, type, name: title } = customProperties[cardField];
            let sortFunction;
            if (type === ECustomPropertyType.NUMBER || type === ECustomPropertyType.DATE) {
                sortFunction = sortCardsByNumericValue;
            } else {
                sortFunction = sortCardsByStringValue;
            }
            cardFieldItem = {
                isHideable: true,
                width: GOOGLE_SPACING * 8,
                isShownByDefault: false,
                title,
                sortFunction,
                sortField: id,
                value: id,
            };
        }
    }
    return cardFieldItem;
};

export const getShownFieldsInitial =
(): ECardField[] => {
    return [...SHOWN_FIELDS_INITIAL];
};
